import { MainMenu } from './MainMenu.js';
import { HUD } from './HUD.js';
import { UiSettingsController } from './UiSettingsController.js';
import { CONFIG } from '../config.js';

/**
 * Phase 12 UI shell: menu → gameplay HUD.
 */
export class GameUI {
  /**
   * @param {{
   *   hudRootSelector: string;
   *   menuRootSelector: string;
   *   graphicsSettings: import('../rendering/GraphicsSettings.js').GraphicsSettings;
   *   onEnterGame: () => void;
   *   onResumeGame: () => void;
   *   onSettingsApplied: () => void;
   *   onUiInteract?: () => void;
   * }} options
   */
  constructor({
    hudRootSelector,
    menuRootSelector,
    graphicsSettings,
    onEnterGame,
    onResumeGame,
    onSettingsApplied,
    onUiInteract = null,
  }) {
    this.settings = new UiSettingsController({ graphicsSettings });
    this.settings.syncGraphicsSettings();

    const menuRoot = document.querySelector(menuRootSelector);
    if (!(menuRoot instanceof HTMLElement)) {
      throw new Error(`Menu root not found: ${menuRootSelector}`);
    }

    this.hud = new HUD({ rootSelector: hudRootSelector });
    this.menu = new MainMenu({
      root: menuRoot,
      settings: this.settings,
      onEnter: () => this._handleEnter(),
      onSettingsChange: onSettingsApplied,
      onUiInteract: () => this._onUiInteract?.(),
    });

    this._sessionActive = false;
    this._onEnterGame = onEnterGame;
    this._onResumeGame = onResumeGame;
    this._onUiInteract = onUiInteract;
  }

  get sessionActive() {
    return this._sessionActive;
  }

  get isMenuOpen() {
    return this.menu.isVisible;
  }

  mount() {
    this.hud.mount();
    this.menu.mount();
    this.hud.hide();
    this.hud.setPointerLocked(false);
  }

  /** @param {{ x: number; y: number; z: number }} position @param {number} fps */
  updateTelemetry(position, fps) {
    if (!this._sessionActive) return;
    this.hud.setTelemetry({ position, fps });
  }

  setPointerLocked(locked) {
    this.hud.setPointerLocked(locked);
  }

  setNpcCount(count) {
    this.hud.setNpcCount(count);
  }

  /** @param {string | null} text */
  setInteractHint(text) {
    this.hud.setInteractHint(text);
  }

  /** @param {number} opacity */
  setScreenFade(opacity) {
    this.hud.setScreenFade(opacity);
  }

  /** @param {string} message */
  flashWorldEvent(message) {
    this.hud.flashWorldEvent(message, CONFIG.events?.glitchDurationMax ?? 2);
  }

  async _handleEnter() {
    if (this._sessionActive && this.isMenuOpen) {
      await this.menu.exitToGame();
      this._onResumeGame();
      return;
    }
    if (this._sessionActive) return;
    this._sessionActive = true;
    await this.menu.exitToGame();
    this.hud.show();
    this._onEnterGame();
  }

  openMenu() {
    if (!this._sessionActive || this.isMenuOpen) return;
    this.menu.show({ resume: true });
    this.setPointerLocked(false);
  }

  dispose() {
    this.menu.dispose();
    this.hud.dispose();
  }
}
